import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import moment from "moment";
import { useForm } from "react-hook-form";
import { DatePicker } from "../../components/others/DatePicker";
import { PrimaryButton } from "../../components/others/ButtonPrimary";
import LavorisService from "../../services/LavorisService";
import absenteeExcelReport from "./absenteeExelReport";
import turnExcelReport from "./turnExcelReport";
import { useAlert } from "../../hooks/useAlert";
import CompanySelectorAsesor from "../../components/others/CompanySelectorAsesor";

const Reports = () => {
  const LavorisInstance = LavorisService.getInstance();
  const [isLoading, setIsLoading] = useState(false)
  const [isAbsenteeLoading, setIsAbsenteeLoading] = useState(false)
  const { currentCompany, companies, currentRole } = useSelector((state: RootState) => state.user);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
    currentCompany?.id || (companies && companies[0]?.id) || ""
  );
  const hr_id = currentCompany?.id || null
  const company_manager = companies && companies[0]?.id || null
  const companyId = currentRole === "ASESOR" ? selectedCompanyId : (hr_id || company_manager)
  const { showAlert } = useAlert();
  const {
    handleSubmit: handleSubmitTurnos,
    control: controlTurnos,
    formState: { errors: errorsTurnos },
  } = useForm<any>();

  // useForm for absentee
  const {
    handleSubmit: handleSubmitAbsentee,
    control: controlAbsentee,
    formState: { errors: errorsAbsentee },
  } = useForm<any>();

  // Actualizar selectedCompanyId cuando cambie currentCompany
  useEffect(() => {
    if (currentCompany?.id) {
      setSelectedCompanyId(currentCompany.id);
    }
  }, [currentCompany]);

  const onSubmitTurnos = async (data: any) => {
    try {
      setIsLoading(true);
      const from = data.fromturnos ? moment(data.fromturnos, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
      const to = data.toturnos ? moment(data.toturnos, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
      let files = await LavorisInstance.TurnServices.getReportes(from, to, companyId)
      if (files.length === 0) {
        showAlert('No se encontraron turnos en las fechas seleccionadas')
      }
      if (files.length > 1) {
        await turnExcelReport(files)
      }

    } catch (error) {
      console.error('Ocurrió un error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const onSubmitAbsentee = async (data: any) => {
    try {
      setIsAbsenteeLoading(true);
      const from = data.fromabsentee ? moment(data.fromabsentee, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
      const to = data.toabsentee ? moment(data.toabsentee, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
      let files = await LavorisInstance.Absences.getReportes(from, to, companyId)

      if (files.length === 0) {
        showAlert('No se encontraron ausentismos en las fechas seleccionadas')
      }
      if (files.length > 1) {
        await absenteeExcelReport(files)
      }
    } catch (error) {
      console.error('Ocurrió un error:', error);
    } finally {
      setIsAbsenteeLoading(false);
    }
  };

  return (
    <div className="main-container">
      <div className="atendoo-card">
        {/* Header Section */}
        <div className="custom-header">
          <div className="header-top">
            <h6 className="subtitle">Reportes</h6>
          </div>
        </div>

        {/* Selector de Compañía para ASESOR */}
        <CompanySelectorAsesor />

        {/* Instructions Section */}
        <div className="bg-gray-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-lg">
          <h5 className='font-semibold mb-3 flex items-center'>
            Instrucciones para generar reportes
          </h5>
          <ul className='space-y-2 text-sm'>
            <li className='flex items-start'>
              <span><strong>TURNOS en Lavoris:</strong> Las fechas son en base a las Fechas del TURNO asignado y se visualizaran todos los casos presentes/admitidos.</span>
            </li>
            <li className='flex items-start'>
              <span><strong>AUSENTISMO:</strong> Las fechas son en base a las Fechas de GESTIÓN del control solicitado y se visualizaran todos los casos completados.</span>
            </li>
          </ul>
        </div>

        {/* Reports Cards Section */}
        <div className='grid md:grid-cols-2 gap-6'>
          {/* Turnos Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="bg-gray-200 text-white p-4 rounded-t-lg">
              <h3 className='font-medium text-lg text-center flex items-center justify-center'>
                Reporte de Turnos
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitTurnos(onSubmitTurnos)}>
                <div className="space-y-4">
                  <div className="flex flex-row gap-2">
                    <DatePicker
                      label="Fecha desde"
                      fieldName="fromturnos"
                      control={controlTurnos}
                      error={errorsTurnos?.fromturnos}
                      minDate={2}
                      maxDate={1}
                      required
                    />
                    <DatePicker
                      label="Fecha hasta"
                      fieldName="toturnos"
                      control={controlTurnos}
                      error={errorsTurnos?.toturnos}
                      minDate={2}
                      maxDate={0}
                      required
                    />
                  </div>

                  <div className="pt-2">
                    <PrimaryButton
                      type="submit"
                      text={isLoading ? "Procesando..." : "Buscar y descargar"}
                      styles="w-full font-semibold text-white bg-red-500 hover:bg-red-600 focus:ring-2 focus:ring-red-300 rounded-lg py-3 px-4 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Ausentismo Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="bg-gray-200 text-white p-4 rounded-t-lg">
              <h3 className='font-medium text-lg text-center flex items-center justify-center'>
                Reporte de Ausentismo
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitAbsentee(onSubmitAbsentee)}>
                <div className="space-y-4">
                  <div className="flex flex-row gap-2">
                    <DatePicker
                      label="Fecha desde"
                      fieldName="fromabsentee"
                      control={controlAbsentee}
                      error={errorsAbsentee?.fromabsentee}
                      minDate={1}
                      maxDate={1}
                      required
                    />
                    <DatePicker
                      label="Fecha hasta"
                      fieldName="toabsentee"
                      control={controlAbsentee}
                      error={errorsAbsentee?.toabsentee}
                      minDate={1}
                      maxDate={0}
                      required
                    />
                  </div>

                  <div className="pt-2">
                    <PrimaryButton
                      type="submit"
                      text={isAbsenteeLoading ? "Procesando..." : "Buscar y descargar"}
                      styles="w-full font-semibold text-white bg-red-500 hover:bg-red-600 focus:ring-2 focus:ring-red-300 rounded-lg py-3 px-4 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      disabled={isAbsenteeLoading}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
};

export default Reports